/**
 * v0.7.9e — 课表「约课」上下文菜单 (辉哥 10/5 拍板)
 *
 * ## 交互 (设计稿 /tmp/v079-design/)
 *   桌面端: 右键时间格 → 气泡菜单
 *   移动端: 长按时间格 500ms → 底部动作面板 (涟漪反馈 + 遮罩)
 *   两端菜单内容随状态切换:
 *     空槽      → [约课]
 *     已有约课  → [修改约课] ---分隔线--- [取消约课(红)]
 *   双击 / 双击触屏: v0.7.9e 曾保留, **v0.8.12 已移除** (辉哥 10/7 16:22 拍板)
 *     —— 菜单是唯一入口, 双击/双触是冗余的第二条同源路径。
 *   单击**不响应** (避免误触, 不干扰横滑滚动)
 *
 * ## 为什么移动端用底部面板而不是气泡
 *   手指本来就遮挡下方内容, 菜单浮在时间格上必然被挡住;
 *   底部面板行高 46px 符合拇指热区, 且跟 iOS/Android ActionSheet 交互一致。
 *
 * ## 取消约课二次确认 (辉哥 10/5 拍板: 可以加上确认)
 *   后端 DeleteLessonController 是软删除 (soft delete + 审计日志 + restore 通道都在),
 *   即便误删也能恢复; 但用户心智上"取消"不可逆感强, 所以加一道确认。
 *
 * @typedef {object} SlotContext
 * @property {?string}  resourceId  - 教室 (FC data-resource-id, 带 r_ 前缀)
 * @property {?string}  dataTime    - 'HH:MM' 槽起始时间
 * @property {?object}  lesson      - 已约课对象 (null = 空槽)
 * @property {string}   roomName    - 教室显示名
 * @property {string}   timeRange   - '10:00 - 11:00' 显示用
 */

// v0.7.9g — 离场动画时长 (ms)
// 必须 ≥ LESS 里 .ZS-ctxmenu / .ZS-ctxmenu-sheet 的 transition 时长 (都是 0.12s),
// 否则动画还没播完 DOM 就被摘掉, 看起来等于没动画。留 10ms 余量。
const EXIT_MS = 130;
const ZS = {
  // ── 菜单 DOM 根 (挂 body, 脱离 FC 的 stacking context) ──
  _root: null,
  // v0.7.9g — 本次打开挂到 document/window 上的解绑函数, close() 时统一回收
  //   改之前 click / keydown 监听是「自己被触发时」才 remove 的, 走 close() 关掉的那一次
  //   永远留在 document 上 —— 每次 open 都多挂一对, 越攒越多 (长按菜单开几十次的场景)
  _detachFns: [],
  _exitTimer: null,
  _ensureRoot() {
    if (this._root && document.body.contains(this._root)) return this._root;
    const r = document.createElement('div');
    r.className = 'ZS-ctxmenu-root';
    document.body.appendChild(r);
    this._root = r;
    return r;
  },
  _destroyRoot() {
    if (this._root && this._root.parentNode) this._root.parentNode.removeChild(this._root);
    this._root = null;
  },
  /** 回收本次打开挂上去的全局监听 + 定时器 (幂等) */
  _detachAll() {
    const fns = this._detachFns || [];
    for (let i = 0; i < fns.length; i++) {
      try {
        fns[i]();
      } catch (e) {/* noop */}
    }
    this._detachFns = [];
    if (this._exitTimer) {
      clearTimeout(this._exitTimer);
      this._exitTimer = null;
    }
  },
  _notifyClosed() {
    if (this._onClosed) {
      const f = this._onClosed;
      this._onClosed = null;
      try {
        f();
      } catch (e) {/* noop */}
    }
  },
  /** 立即销毁, 不播离场动画 —— 只给 open() 复用同一个 root 时用 */
  _forceClose() {
    this._detachAll();
    if (this._root) this._root.classList.remove('ZS-ctxmenu--in', 'ZS-ctxmenu--out');
    this._destroyRoot();
  },
  /** 关掉当前打开的菜单 / 面板 (幂等, 带离场动画) */
  close() {
    this._detachAll();
    this._notifyClosed();
    const root = this._root;
    if (!root) return;
    root.classList.remove('ZS-ctxmenu--in');
    root.classList.add('ZS-ctxmenu--out');
    this._exitTimer = setTimeout(() => {
      this._exitTimer = null;
      // 只销毁「还是这一个」的 root: 期间用户又右键开了新菜单的话别把新菜单摘掉
      if (this._root === root) {
        root.classList.remove('ZS-ctxmenu--out');
        this._destroyRoot();
      }
    }, EXIT_MS);
  },
  /**
   * 取学员显示名
   * ⚠️ v0.7.9e 实测踩坑: 后端 ListLessonsController 返回的是嵌套 `student.display_name`
   *   (见 L95-101 coach/student 预加载), **没有** `student_name` 扁平字段。
   *   只读 `lesson.student_name` 会让线上菜单头/确认框全部显示成「已约课」。
   *   这里按 display_name → username → student_name → id 的顺序兜底。
   */
  _studentName(lesson) {
    if (!lesson) return '';
    const s = lesson.student;
    if (s && (s.display_name || s.username)) return s.display_name || s.username;
    if (lesson.student_name || lesson.studentName) return lesson.student_name || lesson.studentName;
    if (lesson.student_id) return '学员 #' + lesson.student_id;
    return '已约课';
  },
  /** 取教练显示名 (同上, 嵌套结构) */
  _coachName(lesson) {
    if (!lesson) return '';
    const c = lesson.coach;
    if (c && (c.display_name || c.username)) return c.display_name || c.username;
    return lesson.coach_name || '';
  },
  /**
   * 打开菜单
   * @param {SlotContext} ctx
   * @param {object} handlers - { onBook, onEdit, onCancel }
   */
  open(ctx, handlers) {
    // v0.7.9g — 用 _forceClose 而不是 close(): 连开两个菜单时不该先播上一个的淡出再弹新的
    this._forceClose();
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
    const root = this._ensureRoot();
    if (isMobile) {
      root.appendChild(this._buildSheet(ctx, handlers));
      // 强制回流后加 -in 类触发进场动画
      void root.offsetWidth;
      root.classList.add('ZS-ctxmenu--in');
      const off = e => {
        if (e.target === root || e.target.classList.contains('ZS-ctxmenu-scrim')) this.close();
      };
      root.addEventListener('click', off);
      this._detachFns.push(() => root.removeEventListener('click', off));
    } else {
      root.appendChild(this._buildBubble(ctx, handlers));
      // v0.7.9g 修「弹得生硬」—— 缺的就是这一行:
      //   桌面分支原本 appendChild 之后**立刻** add('ZS-ctxmenu--in'), 没有强制回流。
      //   新插入的元素在同一帧内就带上了 --in, 浏览器拿不到「变化前的样式」,
      //   transition 根本不触发 → LESS 里写好的 opacity/scale 动画等于没接上, 表现为生硬弹出。
      //   移动端 sheet 分支一直有 void root.offsetWidth, 所以 sheet 是动的、气泡是生的 ——
      //   同一个 v0.7.9e 实现里两处分支的差异, 不是设计意图。
      void root.offsetWidth;
      root.classList.add('ZS-ctxmenu--in');
      // 监听统一挂进 _detachFns, close() 一次性回收 (含下面那个 setTimeout)
      const t = setTimeout(() => {
        // 点任意处关闭 (capture 阶段, 避免被菜单内部 stopPropagation 影响)
        const offClick = e => {
          if (root.contains(e.target)) return;
          this.close();
        };
        const offKey = e => {
          if (e.key === 'Escape') this.close();
        };
        // v0.7.9g (辉哥 10/5 18:22) — 页面一滚动就关掉菜单
        //   菜单 position:absolute 挂 body 根上按**视口坐标**定位, 课表内容一动它就"飘"在原地,
        //   而它对应的那个时间格已经滚到别处 → 继续留着就是个错位的幽灵菜单。
        //   capture=true 才能收到**所有**可滚动容器的 scroll 事件: 课表本体滚的是
        //   .fc-scroller 而不是 window, 只监听 window 收不到。
        const offScroll = () => this.close();
        // 视口尺寸变了同理 (菜单按视口坐标定位, 尺寸一变位置就对不上了)
        const offResize = () => this.close();
        document.addEventListener('click', offClick, true);
        document.addEventListener('keydown', offKey, true);
        window.addEventListener('scroll', offScroll, true);
        window.addEventListener('resize', offResize);
        this._detachFns.push(() => document.removeEventListener('click', offClick, true), () => document.removeEventListener('keydown', offKey, true), () => window.removeEventListener('scroll', offScroll, true), () => window.removeEventListener('resize', offResize));
      }, 0);
      // setTimeout 本身也要能撤: 否则 close() 发生在同一 tick 内时,
      // 监听会在 root 已被销毁之后才挂上, 彻底收不回来
      this._detachFns.push(() => clearTimeout(t));
    }
  },
  /** 桌面: 跟随光标的气泡菜单 (自动避让视口边缘) */
  _buildBubble(ctx, handlers) {
    const wrap = document.createElement('div');
    wrap.className = 'ZS-ctxmenu';
    const head = document.createElement('div');
    head.className = 'ZS-ctxmenu-head';
    // v0.8.4 — 3 状态: rsv > lesson > 空槽
    // v0.9.2 (辉哥 10/7 拍板) — 固定课模式**不认预留**: 预留块在该模式下压根不渲染,
    //   这里再兜一层 isFixedMode, 是为了防「切模式同一帧 DOM 还没清干净」时露出
    //   「取消预留」入口 —— 那个入口在固定课模式下没有意义 (用户看不见自己取消了什么)。
    const isFixedMode = ctx.mode === 'fixed';
    const hasRsv = !isFixedMode && !!ctx.rsv;
    const has = !!ctx.lesson;
    head.innerHTML = '<span class="ZS-ctxmenu-room"></span><span class="ZS-ctxmenu-time"></span>' + (has ? '<span class="ZS-ctxmenu-who"></span>' : '');
    head.querySelector('.ZS-ctxmenu-room').textContent = ctx.roomName || '未指定教室';
    head.querySelector('.ZS-ctxmenu-time').textContent = ctx.timeRange || '';
    if (has) head.querySelector('.ZS-ctxmenu-who').textContent = this._studentName(ctx.lesson);
    wrap.appendChild(head);
    if (hasRsv) {
      // v0.8.4 — 已预留: [取消预留] 单按钮 (辉哥 10/6 拍板, 无时长栏无原因)
      // v0.17.76 — 项由 handler 驱动 (见上方注释), 没有 onUnreserve 就不画这一项
      if (handlers.onUnreserve) {
        wrap.appendChild(this._item('fas fa-lock-open', '取消预留', () => {
          this.close();
          handlers.onUnreserve && handlers.onUnreserve();
        }, true));
      }
    } else if (has) {
      // ── v0.9.0 固定课课次的菜单文案分流 ────────────────────────────────
      //   决策 B4 + 任务书: 固定课块在菜单里要明示来源, 且**动作语义要说清作用范围**。
      //   正常模式 → 「改这一次 / 取消这一次」 (决策 A3: 仅影响这一条课次)
      //   固定模式 → 「改这次 / 取消这次」 + 拖拽才是整个系列 (避免用户以为菜单也是整组)
      const isFixed = !!(ctx.lesson && ctx.lesson.fixed_lesson_id);
      const editLabel = isFixed ? '改这一次' : '修改约课';
      const cancelLabel = isFixed ? '取消这一次' : '取消约课';
      // v0.17.76 — 两项各自独立 gate; 分隔线只在**两项都在**时才画 (学员两项都没有 ⇒ 整块空)
      if (handlers.onEdit) {
        wrap.appendChild(this._item('fas fa-pen-to-square', editLabel, () => {
          this.close();
          handlers.onEdit();
        }));
      }
      if (handlers.onEdit && handlers.onCancel) {
        wrap.appendChild(this._sep());
      }
      if (handlers.onCancel) {
        wrap.appendChild(this._item('fas fa-trash', cancelLabel, () => {
          this.close();
          handlers.onCancel();
        }, true));
      }
    } else {
      // v0.8.4 — 空槽: [约课] [预留] 双按钮 (辉哥 10/6 拍板)
      // v0.9.0 — 固定课模式下多一项「添加固定课」(决策 B5: 正常模式**不能**新建固定课)
      // v0.17.3 (辉哥拍板) — 固定课模式**不提供**「约课」入口: 固定课是整周模板,
      //   单次临时约课在固定课 tab 里语义冲突, 且跟「添加固定课」重复。
      //   照 233 行「预留」的既有写法用 isFixedMode 兜一层, 只隐藏入口, 不动数据。
      //   ⚠️ 空槽的两种形态要保持分隔线不悬空:
      //     正常模式 = [约课] sep [预留]      (分隔线在两者之间)
      //     固定模式 = [添加固定课]            (整块 0 根 sep; 下面「检查排课」自带 sep)
      //   v0.17.76 — 「约课」也改成 handler 驱动 ⇒ 空槽可以是**只有约课**的形态 (学员视图),
      //     此时一根 sep 都不画 (不能留下悬空分隔线)。
      if (isFixedMode) {
        if (handlers.onAddFixed) {
          wrap.appendChild(this._item('fas fa-repeat', '添加固定课', () => {
            this.close();
            handlers.onAddFixed();
          }));
        }
      } else {
        if (handlers.onBook) {
          wrap.appendChild(this._item('fas fa-calendar-plus', '约课', () => {
            this.close();
            handlers.onBook();
          }));
          if (handlers.onReserve) {
            wrap.appendChild(this._sep());
            wrap.appendChild(this._item('fas fa-lock', '预留', () => {
              this.close();
              handlers.onReserve && handlers.onReserve();
            }, 'is-reserve'));
          }
        }
      }
    }

    // ── v0.9.0: 固定课模式下常驻「检查排课」 ──────────────────────────────
    //   放最底部、跟其它项隔开: 它是**排查工具**不是排课动作, 混排在一起容易被误点。
    if (ctx.mode === 'fixed' && handlers.onCheckFixed) {
      wrap.appendChild(this._sep());
      wrap.appendChild(this._item('fas fa-clipboard-check', '检查排课', () => {
        this.close();
        handlers.onCheckFixed();
      }, 'is-check'));
    }

    // 定位: 跟随光标, 越界翻转
    requestAnimationFrame(() => {
      const pos = this._cursor || {
        x: 0,
        y: 0
      };
      const r = wrap.getBoundingClientRect();
      const vw = window.innerWidth,
        vh = window.innerHeight;
      let left = pos.x,
        top = pos.y;
      if (left + r.width + 8 > vw) left = Math.max(8, vw - r.width - 8);
      if (top + r.height + 8 > vh) top = Math.max(8, vh - r.height - 8);
      wrap.style.left = left + 'px';
      wrap.style.top = top + 'px';
      // v0.7.9g — 缩放原点跟着翻转走。
      //   右键点右下角时菜单会往左上翻转, 这时如果还用 LESS 里写死的 top left 做原点,
      //   动画就是"菜单左上角原地缩小" —— 看着像凭空冒出来。
      //   跟着翻转把原点挪到实际贴着光标的那个角, 视觉上才是"从光标位置展开"。
      const flippedX = left !== pos.x;
      const flippedY = top !== pos.y;
      wrap.style.transformOrigin = (flippedY ? 'bottom' : 'top') + ' ' + (flippedX ? 'right' : 'left');
    });
    return wrap;
  },
  /** 移动: 底部动作面板 */
  _buildSheet(ctx, handlers) {
    const frag = document.createDocumentFragment();
    const scrim = document.createElement('div');
    scrim.className = 'ZS-ctxmenu-scrim';
    frag.appendChild(scrim);
    const sheet = document.createElement('div');
    sheet.className = 'ZS-ctxmenu-sheet';
    const grab = document.createElement('div');
    grab.className = 'ZS-ctxmenu-grab';
    sheet.appendChild(grab);
    const head = document.createElement('div');
    head.className = 'ZS-ctxmenu-sheethead';
    // v0.8.4 — 3 状态: rsv > lesson > 空槽
    // v0.9.2 (辉哥 10/7 拍板) — 固定课模式**不认预留**: 预留块在该模式下压根不渲染,
    //   这里再兜一层 isFixedMode, 是为了防「切模式同一帧 DOM 还没清干净」时露出
    //   「取消预留」入口 —— 那个入口在固定课模式下没有意义 (用户看不见自己取消了什么)。
    const isFixedMode = ctx.mode === 'fixed';
    const hasRsv = !isFixedMode && !!ctx.rsv;
    const has = !!ctx.lesson;
    const coach = has ? this._coachName(ctx.lesson) : '';
    const title = has ? this._studentName(ctx.lesson) : hasRsv ? '已预留' : ctx.roomName || '未指定教室';
    const sub = has ? (ctx.roomName || '') + ' · ' + (ctx.timeRange || '') + (coach ? ' · ' + coach : '') : hasRsv ? (ctx.roomName || '') + ' · ' + (ctx.timeRange || '') : (ctx.timeRange || '') + ' · 空时段';
    head.innerHTML = '<div class="ZS-ctxmenu-stitle"></div><div class="ZS-ctxmenu-ssub"></div>';
    head.querySelector('.ZS-ctxmenu-stitle').textContent = title;
    // 🔁 emoji 换 FA 图标 (辉哥 10/19)。⛔ **不许改成 innerHTML** ——
    //   sub 里拼了 ctx.roomName / coach (`_coachName(...)`, 取自数据库用户名/昵称),
    //   是用户可控内容; 用 innerHTML 拼进去就是 XSS。
    //   文字一律走 textContent, 只把图标元素用 DOM API 追加 (跟本文件 _sheetItem 同一套路)。
    const subEl = head.querySelector('.ZS-ctxmenu-ssub');
    subEl.textContent = sub;
    // v0.9.0 — 固定课课次在菜单头明示来源 (决策 B4)
    if (has && ctx.lesson.fixed_lesson_id) {
      const tag = document.createElement('span');
      const fixedIc = document.createElement('i');
      fixedIc.className = 'fas fa-repeat';
      fixedIc.setAttribute('aria-hidden', 'true');
      tag.appendChild(fixedIc);
      tag.appendChild(document.createTextNode(' 固定课'));
      subEl.appendChild(document.createTextNode(' · '));
      subEl.appendChild(tag);
    }
    sheet.appendChild(head);
    if (hasRsv) {
      // v0.8.4 — 已预留: [取消预留] 单按钮
      // v0.17.76 — 跟桌面气泡同口径: 项由 handler 驱动, 没有就不画
      if (handlers.onUnreserve) {
        sheet.appendChild(this._sheetItem('fas fa-lock-open', '取消预留', () => {
          this.close();
          handlers.onUnreserve && handlers.onUnreserve();
        }, true));
      }
    } else if (has) {
      // v0.9.0 — 固定课课次文案分流 (跟桌面气泡同款, 见 _buildBubble 注释)
      const isFixed = !!(ctx.lesson && ctx.lesson.fixed_lesson_id);
      // v0.17.76 — 学员两项都没有 ⇒ 这一整块不画 (面板只剩标题 + 取消按钮)
      if (handlers.onEdit) {
        sheet.appendChild(this._sheetItem('fas fa-pen-to-square', isFixed ? '改这一次' : '修改约课', () => {
          this.close();
          handlers.onEdit();
        }));
      }
      if (handlers.onCancel) {
        sheet.appendChild(this._sheetItem('fas fa-trash', isFixed ? '取消这一次' : '取消约课', () => {
          this.close();
          handlers.onCancel();
        }, true));
      }
    } else {
      // v0.9.0 — 固定课模式多「添加固定课」(决策 B5)
      // v0.17.3 (辉哥拍板) — 固定课模式**不提供**「约课」入口 (跟桌面气泡同款, 见上方注释)
      //   本方法不画分隔线 (底部面板没有 _sep), 所以不存在悬空 sep 的问题。
      //   v0.17.76 — 「约课」同样 handler 驱动 ⇒ 学员面板只有「约课」一项
      if (isFixedMode) {
        if (handlers.onAddFixed) {
          sheet.appendChild(this._sheetItem('fas fa-repeat', '添加固定课', () => {
            this.close();
            handlers.onAddFixed();
          }));
        }
      } else {
        // v0.8.4 — 空槽: [约课] [预留] 双按钮
        if (handlers.onBook) {
          sheet.appendChild(this._sheetItem('fas fa-calendar-plus', '约课', () => {
            this.close();
            handlers.onBook();
          }));
        }
        if (handlers.onReserve) {
          sheet.appendChild(this._sheetItem('fas fa-lock', '预留', () => {
            this.close();
            handlers.onReserve && handlers.onReserve();
          }));
        }
      }
    }

    // v0.9.0 — 移动端同样给「检查排课」(跟桌面同款, 放在取消按钮上方)
    if (ctx.mode === 'fixed' && handlers.onCheckFixed) {
      sheet.appendChild(this._sheetItem('fas fa-clipboard-check', '检查排课', () => {
        this.close();
        handlers.onCheckFixed();
      }, 'is-check'));
    }
    const cancel = document.createElement('div');
    cancel.className = 'ZS-ctxmenu-cancel';
    cancel.textContent = '取消';
    cancel.addEventListener('click', () => this.close());
    sheet.appendChild(cancel);
    frag.appendChild(sheet);
    return frag;
  },
  /**
   * v0.7.9h — 图标从 emoji 换成 Font Awesome (辉哥 10/5 18:39)
   *   emoji 在暗色 UI 下有三个问题: 各平台字形不一致 (Windows 单色轮廓 / macOS 彩色)、
   *   尺寸和基线对不齐、跟扩展里其它 20+ 个 FA 图标风格割裂。
   *   统一走 FA6 (扩展全站已是 FA6 命名: fa-xmark / fa-triangle-exclamation / fa-house…),
   *   并复用扩展别处已在用的图标名保持视觉一致:
   *     约课     fa-calendar-plus
   *     修改约课 fa-pen-to-square
   *     取消约课 fa-trash
   *   icon 参数改成传 class 名而不是字形, 渲染成 <i class="fas fa-xxx">。
   *   ⚠️ 用 innerHTML 在这里是安全的: 入参全是本文件里的字面量, 不含任何用户输入。
   */
  _item(icon, label, onClick, danger) {
    const it = document.createElement('div');
    // v0.8.4 — danger 接受 string (className) 或 boolean, 兼容 'is-reserve' 这种非 danger 样式
    let dangerClass = '';
    if (danger === true) dangerClass = ' is-danger';else if (typeof danger === 'string' && danger) dangerClass = ' ' + danger;
    it.className = 'ZS-ctxmenu-item' + dangerClass;
    it.innerHTML = '<span class="ZS-ctxmenu-ic"><i class="' + icon + '"></i></span><span class="ZS-ctxmenu-lb"></span>';
    it.querySelector('.ZS-ctxmenu-lb').textContent = label;
    it.addEventListener('click', onClick);
    return it;
  },
  /**
   * v0.9.0 — danger 参数扩展成也接受 className 字符串 (跟 _item 同款)
   *
   * 改前只有 `(danger ? ' is-danger' : '')`, 传字符串 'is-check' 也会被当成真值
   * → 渲染成 is-danger (红字), 排查工具被画成危险操作, 语义反了。
   */
  _sheetItem(icon, label, onClick, danger) {
    const it = document.createElement('div');
    let extra = '';
    if (danger === true) extra = ' is-danger';else if (typeof danger === 'string' && danger) extra = ' ' + danger;
    it.className = 'ZS-ctxmenu-sitem' + extra;
    // 右侧箭头也从 › 字符换成 FA, 跟左侧图标同一套字形
    it.innerHTML = '<span class="ZS-ctxmenu-ic"><i class="' + icon + '"></i></span>' + '<span class="ZS-ctxmenu-lb"></span>' + '<i class="fas fa-chevron-right ZS-ctxmenu-ar"></i>';
    it.querySelector('.ZS-ctxmenu-lb').textContent = label;
    it.addEventListener('click', e => {
      e.stopPropagation();
      onClick();
    });
    return it;
  },
  _sep() {
    const s = document.createElement('div');
    s.className = 'ZS-ctxmenu-sep';
    return s;
  },
  /** 桌面: 记录右键光标位置 (气泡跟随) */
  setCursor(x, y) {
    this._cursor = {
      x,
      y
    };
  },
  /**
   * 轻量 toast (v0.7.9e 补)
   *
   * ⚠️ 为什么自己写而不用 app.flash:
   *   Flarum 2.0 的 frontend **没有** app.flash —— 实测 `typeof app.flash === 'undefined'`
   *   (vendor/flarum/core/js/src/common/ 下无 flash 模块, 只有 PostStream.js 里一个同名局部变量)。
   *   原写法 `if (app.flash && app.flash.success) app.flash.success(...)` 是静默 no-op,
   *   取消约课成功或失败用户都收不到任何反馈。扩展里的既有约定是原生 alert(),
   *   但暗色 UI 下 alert 极其突兀, 所以自绘跟菜单同风格的 toast。
   *
   * @param {string} msg
   * @param {'success'|'error'} kind
   */
  toast(msg, kind) {
    if (kind === void 0) {
      kind = 'success';
    }
    try {
      // 独立于 _root: 菜单关闭 (_destroyRoot) 不能把 toast 一起带走
      let host = document.querySelector('.ZS-ctxmenu-toasthost');
      if (!host) {
        host = document.createElement('div');
        host.className = 'ZS-ctxmenu-toasthost';
        document.body.appendChild(host);
      }
      const t = document.createElement('div');
      t.className = 'ZS-ctxmenu-toast' + (kind === 'error' ? ' is-error' : '');
      t.textContent = msg;
      host.appendChild(t);
      void t.offsetWidth;
      t.classList.add('is-in');
      setTimeout(() => {
        t.classList.remove('is-in');
        setTimeout(() => {
          if (t.parentNode) t.parentNode.removeChild(t);
          if (host && !host.children.length && host.parentNode) host.parentNode.removeChild(host);
        }, 320);
      }, 2600);
    } catch (e) {/* noop — 反馈失败不能影响主流程 */}
  },
  /**
   * 取消约课二次确认 (辉哥 10/5 拍板: 可以加上确认)
   * 不用 Flarum ConfirmationModal —— 那个要引入新依赖且样式不可控,
   * 这里用同款视觉自绘, 保证跟菜单/面板风格一致。
   * @param {object} lesson
   * @param {Function} onConfirm
   */
  confirmCancel(lesson, onConfirm) {
    // v0.8.4 修: 之前用 this.close(), 但 close() 注册了 130ms 后销毁 root 的 _exitTimer,
    //   dialog 紧接着 attach 到同一个 root, 130ms 后 root 被销毁 → dialog 跟着没了,
    //   表现就是「确认框闪一下就消失」。改 _forceClose(): 不播离场动画, 立即清空,
    //   _ensureRoot() 重新建一个干净 root 给 dialog 用。
    this._forceClose();
    const root = this._ensureRoot();
    root.classList.add('ZS-ctxmenu--in');
    const scrim = document.createElement('div');
    scrim.className = 'ZS-ctxmenu-scrim ZS-ctxmenu-scrim--dialog';
    root.appendChild(scrim);
    const dlg = document.createElement('div');
    dlg.className = 'ZS-ctxmenu-dialog';
    const who = this._studentName(lesson) || '这节课';
    dlg.innerHTML = '<div class="ZS-ctxmenu-dtitle">取消约课</div>' + '<div class="ZS-ctxmenu-dbody"></div>' + '<div class="ZS-ctxmenu-dbtns">' + '<div class="ZS-ctxmenu-dbtn is-ghost">再想想</div>' + '<div class="ZS-ctxmenu-dbtn is-danger">确认取消</div>' + '</div>';
    dlg.querySelector('.ZS-ctxmenu-dbody').textContent = '确定要取消「' + who + '」这节课吗？取消后该时段会重新变为空闲，可再次约课。';
    const ghost = dlg.querySelector('.is-ghost');
    const danger = dlg.querySelector('.is-danger');
    ghost.addEventListener('click', () => this.close());
    danger.addEventListener('click', () => {
      this.close();
      onConfirm();
    });
    scrim.addEventListener('click', () => this.close());
    root.appendChild(dlg);
  },
  /**
   * v0.8.4 — 取消预留二次确认 (辉哥 10/6 拍板, 跟 confirmCancel 同款视觉, 文案不同)
   */
  /**
   * v0.9.0 — 取消固定课「这一次」的二次确认
   *
   * 文案必须说清「只取消这一次, 之后每周照常」—— 否则用户以为把整组固定课停了,
   * 下周发现课还在, 反过来又以为系统没生效。
   *
   * 语义跟 confirmCancel 完全不同 (别混用):
   *   confirmCancel     → DELETE /lessons/{id} = **软删**(误删通道, 进回收站)
   *   confirmCancelOnce → PATCH status=cancelled = **业务取消**(请假通道, 不进回收站)
   */
  confirmCancelOnce(lesson, onConfirm) {
    this._forceClose();
    const root = this._ensureRoot();
    root.classList.add('ZS-ctxmenu--in');
    const scrim = document.createElement('div');
    scrim.className = 'ZS-ctxmenu-scrim ZS-ctxmenu-scrim--dialog';
    root.appendChild(scrim);
    const dlg = document.createElement('div');
    dlg.className = 'ZS-ctxmenu-dialog';
    const who = this._studentName(lesson) || '这节课';
    const date = lesson && lesson.occurrence_date || '';
    dlg.innerHTML = '<div class="ZS-ctxmenu-dtitle">取消这一次</div>' + '<div class="ZS-ctxmenu-dbody"></div>' + '<div class="ZS-ctxmenu-dbtns">' + '<div class="ZS-ctxmenu-dbtn is-ghost">再想想</div>' + '<div class="ZS-ctxmenu-dbtn is-danger">确认取消这一次</div>' + '</div>';
    dlg.querySelector('.ZS-ctxmenu-dbody').textContent = '确定只取消「' + who + '」' + (date ? ' ' + date + ' ' : '') + '这一次吗？该时段会重新变为空闲，之后每周照常上课（不会停掉整个固定课）。';
    const ghost = dlg.querySelector('.is-ghost');
    const danger = dlg.querySelector('.is-danger');
    ghost.addEventListener('click', () => this.close());
    danger.addEventListener('click', () => {
      this.close();
      onConfirm();
    });
    scrim.addEventListener('click', () => this.close());
    root.appendChild(dlg);
  },
  confirmUnreserve(rsv, onConfirm) {
    // v0.8.4 修: 同 confirmCancel, 详见该方法注释
    this._forceClose();
    const root = this._ensureRoot();
    root.classList.add('ZS-ctxmenu--in');
    const scrim = document.createElement('div');
    scrim.className = 'ZS-ctxmenu-scrim ZS-ctxmenu-scrim--dialog';
    root.appendChild(scrim);
    const dlg = document.createElement('div');
    dlg.className = 'ZS-ctxmenu-dialog';
    const t = rsv.start_at ? String(rsv.start_at).slice(11, 16) : '';
    const e = rsv.end_at ? String(rsv.end_at).slice(11, 16) : '';
    dlg.innerHTML = '<div class="ZS-ctxmenu-dtitle">取消预留</div>' + '<div class="ZS-ctxmenu-dbody"></div>' + '<div class="ZS-ctxmenu-dbtns">' + '<div class="ZS-ctxmenu-dbtn is-ghost">再想想</div>' + '<div class="ZS-ctxmenu-dbtn is-danger">确认取消</div>' + '</div>';
    dlg.querySelector('.ZS-ctxmenu-dbody').textContent = '确定要取消 ' + t + ' - ' + e + ' 这个时段预留吗？取消后该时段会重新变为空闲，可再次约课或预留。';
    const ghost = dlg.querySelector('.is-ghost');
    const danger = dlg.querySelector('.is-danger');
    ghost.addEventListener('click', () => this.close());
    danger.addEventListener('click', () => {
      this.close();
      onConfirm();
    });
    scrim.addEventListener('click', () => this.close());
    root.appendChild(dlg);
  }
};
export default ZS;
flarum.reg.add('ziiven-ziven-scheduler', 'forum/utils/SlotContextMenu', ZS);